import { useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { selectIsDrawerDismissed, selectIsDrawerMinimized } from '@/uppy-file-upload/store/selectors'
import { selectHideSidebars } from '@/store/selectors/ui-state.selectors'

type UseUploadDrawerParams = {
  /** The total number of files currently being processed. */
  processingTotalCount: number
  /** Whether there are any active uploads. */
  hasActiveUploads: boolean
}

type UseUploadDrawerReturn = {
  /** Whether the drawer is visible. */
  visible: boolean
  /** Whether the drawer is expanded. */
  expanded: boolean
  /** Whether the desktop sidebars are hidden, used to position the drawer correctly. */
  sidebarHidden: boolean
  /** Called when the files are being dragged. */
  onFilesDrag: (isDragging: boolean) => void
  /** Called when the drawer expanded state should change. */
  onExpandedChange: (expanded: boolean) => void
  /** Called when the drawer close button is clicked. */
  onClose: () => void
}

export function useUploadDrawer({ processingTotalCount, hasActiveUploads }: UseUploadDrawerParams): UseUploadDrawerReturn {
  const dispatch = useAppDispatch()

  // Persisted in Redux so state survives DriveView remounts during folder navigation.
  const userDismissed = useAppSelector(selectIsDrawerDismissed)
  const userMinimized = useAppSelector(selectIsDrawerMinimized)
  const sidebarHidden = useAppSelector(selectHideSidebars)

  const [isDraggingFiles, setIsDraggingFiles] = useState(false)

  // null on first render so existing uploads on remount don't trigger drawerNewFilesAdded.
  const prevProcessingTotalCountRef = useRef<number | null>(null)

  useEffect(() => {
    // If the number of processing files has increased, trigger the drawerNewFilesAdded action.
    const prev = prevProcessingTotalCountRef.current
    if (prev !== null && processingTotalCount > prev) {
      dispatch(UppyFileUploadActions.drawerNewFilesAdded())
    }
    prevProcessingTotalCountRef.current = processingTotalCount
  }, [processingTotalCount, dispatch])

  return {
    visible: isDraggingFiles || (!userDismissed && hasActiveUploads),
    expanded: !userMinimized,
    sidebarHidden,
    onFilesDrag: (isDragging: boolean) => setIsDraggingFiles(isDragging),
    onExpandedChange: (expanded: boolean) => dispatch(UppyFileUploadActions.drawerExpandedChanged(expanded)),
    onClose: () => dispatch(UppyFileUploadActions.drawerDismissed()),
  }
}
